import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row, Space, Statistic, Table, Tag, Typography, message } from "antd";
import { getAdminUserInfo } from "../auth/auth.storage";
import type { ColumnsType } from "antd/es/table";
import {
  AdminAccount,
  AdminLatestCode,
  generateCode,
  generateCodeFor,
  getAdminAccounts,
  getLatestAdminCodes,
} from "../sources/admin.source";

const { Title, Text } = Typography;

const DashboardPage: React.FC = () => {
  const me = getAdminUserInfo();
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [codes, setCodes] = useState<AdminLatestCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  const codeByAdminId = useMemo(() => {
    const map = new Map<string, AdminLatestCode>();
    for (const c of codes) map.set(String(c.adminId), c);
    return map;
  }, [codes]);

  const load = async () => {
    setLoading(true);
    try {
      const [acc, latest] = await Promise.all([getAdminAccounts(), getLatestAdminCodes()]);
      setAccounts(acc);
      setCodes(latest);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Không tải được dữ liệu dashboard";
      message.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doGenerate = async (admin: AdminAccount) => {
    setGeneratingId(admin._id);
    try {
      if (me?.role === "SUPERADMIN") {
        await generateCodeFor(admin._id, 30);
      } else {
        await generateCode(30);
      }
      message.success("Đã tạo code mới");
      await load();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Tạo code thất bại";
      message.error(String(msg));
    } finally {
      setGeneratingId(null);
    }
  };

  const columns: ColumnsType<AdminAccount> = [
    {
      title: "Tài khoản",
      dataIndex: "username",
      key: "username",
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 700 }}>{v}</span>
          <span style={{ color: "rgba(17,17,20,0.55)", fontSize: 12 }}>
            {r.role}
          </span>
        </Space>
      ),
    },
    {
      title: "Code hiện tại",
      key: "code",
      render: (_, r) => {
        const c = codeByAdminId.get(String(r._id));
        if (!c) return <span style={{ color: "rgba(17,17,20,0.55)" }}>Chưa có</span>;
        return (
          <Space>
            <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", fontWeight: 800 }}>
              {c.code}
            </span>
            {c.isExpired ? <Tag color="default">Hết hạn</Tag> : <Tag color="red">Còn hạn</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Hết hạn",
      key: "expiresAt",
      width: 200,
      render: (_, r) => {
        const c = codeByAdminId.get(String(r._id));
        if (!c) return "-";
        return c.expiresAt ? new Date(c.expiresAt).toLocaleString() : "Không hết hạn";
      },
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 260,
      render: (_, r) => {
        const c = codeByAdminId.get(String(r._id));
        const canGenerate =
          me?.role === "SUPERADMIN" || (me?.role === "ADMIN" && r._id === me?.id);

        return (
          <Space>
            <Button
              type="primary"
              loading={generatingId === r._id}
              onClick={() => doGenerate(r)}
              disabled={!canGenerate}
            >
              {c ? "Tạo code mới" : "Tạo code"}
            </Button>
            <Button
              onClick={async () => {
                if (!c?.code) return;
                try {
                  await navigator.clipboard.writeText(c.code);
                  message.success("Đã copy code");
                } catch {
                  message.error("Không copy được (trình duyệt chặn)");
                }
              }}
              disabled={!c?.code}
            >
              Copy
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div>
        <Title level={3} style={{ margin: 0, color: "#111114" }}>
          Dashboard
        </Title>
        <Text style={{ color: "rgba(17,17,20,0.65)" }}>
          Tạo code quản lý và xem danh sách admin/superadmin
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Role" value={me?.role ?? "-"} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Username" value={me?.userName ?? "-"} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Coins" value={me?.coins ?? 0} />
          </Card>
        </Col>
      </Row>

      <Card
        title="Danh sách Admin / Superadmin"
        extra={
          <Button onClick={load} loading={loading}>
            Làm mới
          </Button>
        }
      >
        <Table<AdminAccount>
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={accounts}
          pagination={{ pageSize: 8 }}
        />
        <div style={{ marginTop: 8, color: "rgba(17,17,20,0.55)", fontSize: 12 }}>
          Superadmin có thể tạo code cho mọi admin/superadmin. Admin chỉ tạo được code cho chính mình.
        </div>
      </Card>
    </div>
  );
};

export default DashboardPage;

